import type { ReactElement, ReactNode } from 'react';

import type { TFunction } from 'i18next';

import type { CatalogState } from '@/shared/domain/serviceCatalog';

import type { Tone } from './tone';

import styles from './Pill.module.css';

/* A small status badge for prose contexts — key/value pairs, cards, the Pixie
   panel. Tables draw the same verdicts as scale-tag through the data grid, so
   this is not a table primitive; see tone.ts for the mapping that keeps the two
   renderings saying the same thing. */

export function Pill({
  tone = 'neutral',
  mono = false,
  title,
  children,
}: {
  readonly tone?: Tone;
  readonly mono?: boolean;
  readonly title?: string;
  readonly children: ReactNode;
}): ReactElement {
  const classes = [styles.pill, styles[tone], mono ? styles.mono : ''].filter(Boolean).join(' ');
  return (
    <span className={classes} {...(title === undefined ? {} : { title })}>
      {children}
    </span>
  );
}

/* The wording and weight of a catalog verdict, exported because a grid cell
   draws the same verdict as a scale-tag rather than as a Pill. One map, so the
   two renderings cannot come to say different things.

   The first half of each pair is a catalogue key rather than the word itself,
   the same way `taxonomy.ts` carries structure and leaves the wording to
   `domain`. `catalogLabel` is the only thing that turns it into words, so a
   verdict cannot be drawn in one language on the table and another in the
   panel beside it. */
export type CatalogLabelKey =
  | 'catalogState.matched'
  | 'catalogState.existing'
  | 'catalogState.notFound'
  | 'catalogState.notOrderable'
  | 'catalogState.notApplicable';

export const catalogPills: Readonly<Record<CatalogState, readonly [CatalogLabelKey, Tone]>> = {
  matched: ['catalogState.matched', 'ready'],
  existing: ['catalogState.existing', 'ai'],
  'not-found': ['catalogState.notFound', 'conf'],
  'not-orderable': ['catalogState.notOrderable', 'unsafe'],
  'n/a': ['catalogState.notApplicable', 'neutral'],
};

/** The verdict in words. Takes `t` rather than calling a hook: a grid column is
    built inside a `useMemo`, and a table cell is a value rather than a
    component. */
export function catalogLabel(state: CatalogState, t: TFunction<'domain'>): string {
  return t(catalogPills[state][0]);
}
